import React, { useState } from "react";
import arrowDownIcon from "assets/landing/plusicon.svg";
import { FormInput } from "./FormInput";
import { customShadow } from "./utils/classes";
import { SubmitHandler, useForm } from "react-hook-form";
import clsx from "clsx";
import { toast } from "react-toastify";
import {
  MAILCHIMP_AUDIENCE_ID,
  MAILCHIMP_CARD_TAG_ID,
  MAILCHIMP_FORM_ACTION,
  MAILCHIMP_FORM_ID,
  MAILCHIMP_HONEYPOT_FIELD,
  MAILCHIMP_SUBSCRIBE_VALUE,
  MAILCHIMP_USER_ID,
  STORAGE_KEY,
} from "./utils/constants";

const MERGE_TAGS = {
  NAME: "FNAME",
  CITIZENSHIP: "CITIZEN",
  RESIDENCE: "COUNTRY",
};

interface TFormInput {
  name: string;
  email: string;
  citizenship: string;
  residence: string;
}

function validateEmail(email: string) {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export const CardForm: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<TFormInput>();

  const onSubmit: SubmitHandler<TFormInput> = async (data) => {
    clearErrors();

    if (!validateEmail(data.email)) {
      setError("email", { type: "manual", message: "Invalid email format." });
      return;
    }

    const storageEmail = localStorage.getItem(STORAGE_KEY);
    if (storageEmail === data.email) {
      setError("email", {
        type: "manual",
        message: "You are already in waitlist.",
      });
      return;
    }

    if (
      !MAILCHIMP_FORM_ACTION ||
      !MAILCHIMP_USER_ID ||
      !MAILCHIMP_AUDIENCE_ID
    ) {
      setError("email", {
        type: "manual",
        message: "Subscription service keys are missing.",
      });
      return;
    }

    try {
      const formData = new FormData();

      formData.append("u", MAILCHIMP_USER_ID as string);
      formData.append("id", MAILCHIMP_AUDIENCE_ID as string);
      formData.append("f_id", MAILCHIMP_FORM_ID as string);
      formData.append("subscribe", MAILCHIMP_SUBSCRIBE_VALUE);

      formData.append("EMAIL", data.email);
      formData.append(MERGE_TAGS.NAME, data.name as string);
      formData.append(MERGE_TAGS.CITIZENSHIP, data.citizenship as string);
      formData.append(MERGE_TAGS.RESIDENCE, data.residence as string);

      if (MAILCHIMP_CARD_TAG_ID) {
        formData.append("tags", MAILCHIMP_CARD_TAG_ID);
      }

      if (MAILCHIMP_HONEYPOT_FIELD) {
        formData.append(MAILCHIMP_HONEYPOT_FIELD, "");
      }

      await fetch(MAILCHIMP_FORM_ACTION as string, {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });

      localStorage.setItem(STORAGE_KEY, data.email);
      reset();
      setIsSuccess(true);
      toast.success("Successfully subscribed!");
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      setError("email", {
        type: "manual",
        message: "Something went wrong. Please try again.",
      });
      setTimeout(() => clearErrors("email"), 5000);
      toast.error("Subscription failed.");
    }
  };

  return (
    <article
      className={`hero-card bg-[#F3F3F8] flex flex-col relative z-20 mx-auto ${customShadow} 
        w-[36.11vw] p-[1.25vw] gap-[1.67vw] !rounded-[2.93vw]
        max-lg:w-[93.6vw] max-lg:h-[120.5333vw] max-lg:p-[4.8vw] max-lg:gap-[4.2667vw] max-lg:!rounded-[8.0vw]
      `}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        action={MAILCHIMP_FORM_ACTION || undefined}
        method="post"
        className="flex flex-col max-lg:gap-[4.27vw] gap-[1.67vw]"
      >
        <FormInput
          label="Your Name"
          name="name"
          placeholder="Enter your Name"
          register={register}
          requiredMessage="Please enter your name."
          error={errors.name}
        />
        <FormInput
          label="Your Email"
          name="email"
          type="email"
          placeholder="email@mail.com"
          register={register}
          requiredMessage="Please enter your email."
          error={errors.email}
        />
        <FormInput
          label="Your Citizenship"
          name="citizenship"
          placeholder="Citizenship"
          register={register}
          requiredMessage="Please enter your citizenship."
          error={errors.citizenship}
        />
        <FormInput
          label="Your Country of Residence"
          name="residence"
          placeholder="Country of residence"
          register={register}
          requiredMessage="Please enter your country of residence."
          error={errors.residence}
        />

        <button
          type="submit"
          disabled={isSubmitting}
          className={clsx(
            `h-[3.9vw] mt-auto pr-[0.7vw] flex items-center pl-[1.5vw] justify-between gap-[1.142vw] install-agnt 
            !rounded-[1.11vw] max-lg:w-[84.0vw] max-lg:h-[12.8vw] max-lg:pr-[2.1333vw] max-lg:pl-[4.2667vw] 
            max-lg:gap-[3.2vw] max-lg:!rounded-[3.2vw] max-lg:py-[2.1333vw]`,
            isSubmitting && "opacity-75 cursor-not-allowed"
          )}
        >
          <span
            className="text-white font-luxora-medium leading-[1.1] text-[1.39vw] font-normal 
            max-lg:text-[4.8vw]"
          >
            {isSubmitting
              ? "Submitting..."
              : isSuccess
              ? "Subscribed!"
              : "Join Waitlist"}
          </span>
          <div
            className="size-[2.85vw] rounded-[0.65vw] flex items-center justify-center bg-white shadow-custom
            max-lg:size-[8.5333vw] max-lg:rounded-[3.2vw]"
          >
            <img
              src={arrowDownIcon}
              alt="arrow down"
              className="size-[1.7vw] max-lg:size-[4.75vw]"
            />
          </div>
        </button>
      </form>
    </article>
  );
};
